import './polyfills/polyfills';
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { vuetify } from '@/plugins/vuetify';
import { i18n } from '@/plugins/i18n';
import './styles.css';
import { createPinia } from 'pinia';
import { useQueryClient, VueQueryPlugin } from '@tanstack/vue-query';
import { RouteName } from '@/router/routes';
import { useSessionStore } from '@/stores/useSessionStore';
import { fetchLoggedInUser, LOGGED_IN_USER_QUERY_KEY } from '@/api/useLoggedInUser';
import { isAuthorized } from '@/utils/authorizationUtils';

const pinia = createPinia();
const app = createApp(App);

app.use(router);
app.use(pinia);
app.use(VueQueryPlugin);
app.use(vuetify);
app.use(i18n);

router.isReady().then(async () => {
  app.mount('#app');
});

router.beforeResolve(async (to, from, next) => {
  const $session = useSessionStore();
  const isAuthenticated = $session._session.isAuthenticated;

  if (to.path === '/' || to.path === '') {
    return next({ name: RouteName.Phone });
  }

  // Handle authentication required routes
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({
      name: RouteName.Login,
      query: to.path !== '/' ? { redirect: to.path } : undefined,
    });
  }
  //Handle users with active existing credentials going accessing Login Page
  if (to.name === RouteName.Login && isAuthenticated) {
    return next({ name: RouteName.Phone });
  }

  //Handle routes that requires permissions
  if (to.meta.requiredPermissions && isAuthenticated) {
    const loggedInUser = await useQueryClient().ensureQueryData({
      queryKey: [LOGGED_IN_USER_QUERY_KEY],
      queryFn: () => fetchLoggedInUser(),
    });

    if (!isAuthorized(loggedInUser, to.meta.requiredPermissions)) {
      return next({ name: RouteName.Phone });
    }
  }

  return next();
});

// Track if we've already attempted a reload to prevent infinite loops
let hasReloaded = false;

// Fix for dynamic imports error during deployments. When a new version is released
// and user is using a cached script file that tries to fetch an old chunk from the
// server that is no longer available, the error will appear. We handle this by
// catching the error and reloading the app to fetch the updated chunks.

// fore more details check: https://github.com/vitejs/vite/issues/11804
router.onError((error, _) => {
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    // Prevent infinite reload loop
    if (hasReloaded) {
      console.error('Already attempted reload, stopping to prevent loop');
      return;
    }

    console.error('Dynamic import failed, reloading page');
    hasReloaded = true;

    window.location.reload();
  }
});

// Reset the reload flag when navigation succeeds
router.afterEach(() => {
  hasReloaded = false;
});
